const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  multipart?: boolean;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, multipart = false } = options;

  const headers: Record<string, string> = {};
  if (!multipart) headers['Content-Type'] = 'application/json';

  if (auth) {
    const tokens = localStorage.getItem('tokens');
    if (tokens) {
      const { access } = JSON.parse(tokens);
      headers['Authorization'] = `Bearer ${access}`;
    }
  }

  const fetchBody = multipart
    ? (body as FormData | undefined)
    : body
    ? JSON.stringify(body)
    : undefined;

  const res = await fetch(`${API_BASE}${endpoint}`, { method, headers, body: fetchBody });

  if (res.status === 401 && auth) {
    const refreshed = await refreshToken();
    if (refreshed) {
      const tokens = JSON.parse(localStorage.getItem('tokens')!);
      headers['Authorization'] = `Bearer ${tokens.access}`;
      const retry = await fetch(`${API_BASE}${endpoint}`, { method, headers, body: fetchBody });
      if (!retry.ok) {
        const err = await retry.json().catch(() => ({ error: 'Request failed' }));
        throw err;
      }
      return retry.status === 204 ? ({} as T) : retry.json();
    }
    localStorage.removeItem('tokens');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw err;
  }

  return res.status === 204 ? ({} as T) : res.json();
}

async function refreshToken(): Promise<boolean> {
  const tokens = localStorage.getItem('tokens');
  if (!tokens) return false;

  const { refresh } = JSON.parse(tokens);
  try {
    const res = await fetch(`${API_BASE}/accounts/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    const existing = JSON.parse(tokens);
    localStorage.setItem('tokens', JSON.stringify({ ...existing, access: data.access }));
    return true;
  } catch {
    return false;
  }
}

// Verification (merchant paid → admin approved → tick badge)

export type VerificationStatus =
  | 'pending_payment' | 'pending_review' | 'approved' | 'rejected' | 'failed' | 'cancelled';

export interface VerificationDocument {
  id: number;
  file: string | null;
  file_url: string | null;
  label: string;
  created_at: string;
}

export interface VerificationRequestItem {
  id: number;
  reference: string;
  user: number;
  user_name: string;
  user_email: string;
  user_phone: string;
  user_country: string;
  company_name: string;
  amount: string;
  currency: string;
  status: VerificationStatus;
  status_display: string;
  business_name: string;
  business_description: string;
  business_notes: string;
  support_doc_url: string;
  documents: VerificationDocument[];
  pesapal_order_tracking_id: string;
  pesapal_payment_method: string;
  pesapal_confirmation_code: string;
  paid_at: string | null;
  reviewed_by: number | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  admin_notes: string;
  created_at: string;
  updated_at: string;
}

export interface VerificationSettingItem {
  fee: string;
  currency: string;
  description: string;
  is_active: boolean;
  updated_at: string;
}

export const verificationApi = {
  setting: () => request<VerificationSettingItem>('/accounts/verification/settings/', { auth: false }),
  updateSetting: (body: Partial<VerificationSettingItem>) =>
    request<VerificationSettingItem>('/accounts/verification/settings/', { method: 'PATCH', body }),
  list: (status?: VerificationStatus) => {
    const qs = status ? `?status=${status}` : '';
    return request<VerificationRequestItem[]>(`/accounts/verification/requests/${qs}`);
  },
  create: (data: FormData) =>
    request<{ request: VerificationRequestItem; redirect_url: string | null; order_tracking_id: string }>(
      '/accounts/verification/request/',
      { method: 'POST', body: data, multipart: true }
    ),
  approve: (id: number, admin_notes?: string) =>
    request<VerificationRequestItem>(`/accounts/verification/${id}/approve/`, {
      method: 'POST',
      body: { admin_notes: admin_notes || '' },
    }),
  reject: (id: number, admin_notes: string) =>
    request<VerificationRequestItem>(`/accounts/verification/${id}/reject/`, {
      method: 'POST',
      body: { admin_notes },
    }),
};

// Member interest (merchant → member upgrade)

export type MemberInterestStatus = 'pending' | 'responded' | 'promoted' | 'rejected';

export interface MemberInterestItem {
  id: number;
  user: number;
  user_role: string;
  user_email: string;
  user_company: string;
  full_name: string;
  email: string;
  phone: string;
  company_name: string;
  country: string;
  business_category: string;
  notes: string;
  requested_package: number | null;
  requested_package_name: string | null;
  requested_package_price: string | null;
  status: MemberInterestStatus;
  status_display: string;
  suggested_package: number | null;
  suggested_package_name: string | null;
  suggested_package_price: string | null;
  admin_message: string;
  admin_responded_at: string | null;
  created_at: string;
  updated_at: string;
}

export const memberInterestApi = {
  myInterest: () =>
    request<{ interest: MemberInterestItem | null }>('/accounts/me/member-interest/'),
  submit: (data: {
    full_name: string;
    email: string;
    phone?: string;
    company_name?: string;
    country?: string;
    business_category?: string;
    notes?: string;
    requested_package?: number | null;
  }) =>
    request<MemberInterestItem>('/accounts/me/member-interest/', {
      method: 'POST',
      body: data,
    }),
  adminList: (status?: MemberInterestStatus) => {
    const qs = status ? `?status=${status}` : '';
    return request<MemberInterestItem[]>(`/accounts/admin/member-interests/${qs}`);
  },
  adminRespond: (id: number, body: { package_id?: number | null; message: string }) =>
    request<MemberInterestItem>(`/accounts/admin/member-interests/${id}/respond/`, {
      method: 'POST',
      body,
    }),
  adminReject: (id: number, message: string) =>
    request<MemberInterestItem>(`/accounts/admin/member-interests/${id}/reject/`, {
      method: 'POST',
      body: { message },
    }),
  adminPromote: (userId: number, package_id?: number | null) =>
    request<{ user: unknown; payment: unknown }>(`/accounts/admin/users/${userId}/promote-to-member/`, {
      method: 'POST',
      body: package_id ? { package_id } : {},
    }),
};

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    request<{ message: string; email: string; email_sent: boolean }>(
      '/accounts/login/',
      { method: 'POST', body: { email, password }, auth: false }
    ),

  verifyOtp: (email: string, otp: string) =>
    request<{ access: string; refresh: string; user: User }>(
      '/accounts/verify-otp/',
      { method: 'POST', body: { email, otp }, auth: false }
    ),

  resendOtp: (email: string) =>
    request<{ message: string; email_sent: boolean }>(
      '/accounts/resend-otp/',
      { method: 'POST', body: { email }, auth: false }
    ),

  me: () => request<User>('/accounts/me/'),

  updateMe: (data: FormData) =>
    request<User>('/accounts/me/', { method: 'PATCH', body: data, multipart: true }),

  publicSeller: (key: string | number) =>
    request<PublicSeller>(`/accounts/sellers/${encodeURIComponent(String(key))}/`, { auth: false }),

  listReviews: (key: string | number) =>
    request<ReviewItem[]>(`/accounts/sellers/${encodeURIComponent(String(key))}/reviews/`, { auth: false }),

  createReview: (key: string | number, input: ReviewCreateInput) =>
    request<ReviewItem>(`/accounts/sellers/${encodeURIComponent(String(key))}/reviews/`, {
      method: 'POST', body: input, auth: false,
    }),

  changePassword: (old_password: string, new_password: string) =>
    request<{ message: string }>('/accounts/change-password/', {
      method: 'POST',
      body: { old_password, new_password },
    }),

  // Forgot password — step 1 emails a code, step 2 sets the new password.
  // Step 1 always succeeds for a well-formed address (no user enumeration).
  requestPasswordReset: (email: string) =>
    request<{ message: string; email: string }>('/accounts/password-reset/', {
      method: 'POST', body: { email }, auth: false,
    }),

  confirmPasswordReset: (email: string, otp: string, new_password: string) =>
    request<{ message: string }>('/accounts/password-reset/confirm/', {
      method: 'POST', body: { email, otp, new_password }, auth: false,
    }),

  memberRegister: (data: MemberRegisterData) =>
    request<{ message: string; access: string; refresh: string; user: User; payment: PaymentRecord }>(
      '/accounts/register/',
      { method: 'POST', body: data, auth: false }
    ),

  checkout: (reference?: string) =>
    request<{ redirect_url: string; order_tracking_id: string; reference: string }>(
      '/accounts/checkout/',
      {
        method: 'POST',
        body: reference ? { reference } : {},
        // Self-registered users have no JWT yet (account is inactive pending
        // payment). With a reference we hit the endpoint anonymously.
        auth: !reference,
      }
    ),

  paymentStatus: () =>
    request<{
      has_payment: boolean;
      status: string | null;
      payment: PaymentRecord | null;
      receipt_number: string | null;
      has_subscription: boolean;
      paid_at: string | null;
      expires_at: string | null;
      is_expired: boolean;
      days_until_expiry: number;
      total_paid: string;
      package_price: string;
      balance_due: string;
      currency: string;
    }>(
      '/accounts/payment-status/'
    ),

  renew: () =>
    request<{ message: string; payment: PaymentRecord }>(
      '/accounts/renew/',
      { method: 'POST', body: {} }
    ),

  paymentCallback: (orderTrackingId: string, orderMerchantRef: string) =>
    // PII-light response — only the fields the success page needs to render
    // and route the visitor. Authenticated profile fetched via /me/.
    request<{ status: string; reference: string; receipt_number: string | null; role: string }>(
      `/accounts/pesapal/callback/?OrderTrackingId=${orderTrackingId}&OrderMerchantReference=${orderMerchantRef}`,
      { auth: false }
    ),

  receipt: (reference: string) =>
    request<ReceiptData>(`/accounts/receipt/${reference}/`),

  vendorRegister: (data: VendorRegisterData) =>
    request<{ message: string; access: string; refresh: string; user: User; payment: PaymentRecord }>(
      '/accounts/register/vendor/',
      { method: 'POST', body: data, auth: false }
    ),

  // Verification (merchant-paid, admin-approved)
  getVerificationSetting: () =>
    request<VerificationSetting>('/accounts/verification/settings/', { auth: false }),
  updateVerificationSetting: (patch: Partial<VerificationSetting>) =>
    request<VerificationSetting>('/accounts/verification/settings/', { method: 'PATCH', body: patch }),
  listVerificationRequests: () =>
    request<VerificationRequestItem[]>('/accounts/verification/requests/'),
  createVerificationRequest: (input: { business_notes?: string; support_doc_url?: string }) =>
    request<{ request: VerificationRequestItem; redirect_url?: string; order_tracking_id?: string }>(
      '/accounts/verification/request/',
      { method: 'POST', body: input }
    ),
  verificationCallback: (params: { OrderTrackingId?: string; OrderMerchantReference?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ status: VerificationStatus; reference: string; request: VerificationRequestItem }>(
      `/accounts/verification/callback/?${q}`, { auth: false }
    );
  },
  approveVerification: (id: number, admin_notes?: string) =>
    request<VerificationRequestItem>(`/accounts/verification/${id}/approve/`, {
      method: 'POST', body: { admin_notes: admin_notes || '' },
    }),
  rejectVerification: (id: number, admin_notes?: string) =>
    request<VerificationRequestItem>(`/accounts/verification/${id}/reject/`, {
      method: 'POST', body: { admin_notes: admin_notes || '' },
    }),
};

export interface VerificationSetting {
  fee: string;
  currency: string;
  description: string;
  is_active: boolean;
  updated_at: string;
}

export type VerificationStatus =
  | 'pending_payment' | 'pending_review' | 'approved' | 'rejected' | 'failed' | 'cancelled';

export interface VerificationRequestItem {
  id: number;
  reference: string;
  user: number;
  user_name: string;
  user_email: string;
  company_name: string;
  amount: string;
  currency: string;
  status: VerificationStatus;
  business_notes: string;
  support_doc_url: string;
  pesapal_order_tracking_id: string;
  pesapal_payment_method: string;
  pesapal_confirmation_code: string;
  paid_at: string | null;
  reviewed_by: number | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  admin_notes: string;
  created_at: string;
  updated_at: string;
}

// Admin API
export const adminApi = {
  listUsers: (params?: { role?: string; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.role) query.set('role', params.role);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    return request<User[]>(`/accounts/users/${qs ? `?${qs}` : ''}`);
  },

  createUser: (data: CreateUserData) =>
    request<User>('/accounts/users/', { method: 'POST', body: data }),

  getUser: (id: number) => request<User>(`/accounts/users/${id}/`),

  updateUser: (id: number, data: Partial<User> & UserAdminPatch) =>
    request<User>(`/accounts/users/${id}/`, { method: 'PATCH', body: data }),

  deleteUser: (id: number) =>
    request<void>(`/accounts/users/${id}/`, { method: 'DELETE' }),

  renewUser: (id: number) =>
    request<{ message: string }>(`/accounts/admin/users/${id}/renew/`, { method: 'POST', body: {} }),

  setUserAccess: (id: number, feature: 'crowdfunding' | 'jobs', grant: boolean) =>
    request<{
      id: number; email: string; feature: string; granted: boolean;
      can_crowdfund: boolean; can_post_jobs: boolean;
    }>(`/accounts/admin/users/${id}/access/`, {
      method: 'POST',
      body: { feature, grant },
    }),

  createMember: (data: AdminCreateMemberData) =>
    request<{ message: string; user: User; email_sent: boolean }>(
      '/accounts/admin/members/create/',
      { method: 'POST', body: data }
    ),

  createMerchant: (data: AdminCreateMerchantData) =>
    request<{ message: string; user: User; email_sent: boolean }>(
      '/accounts/admin/merchants/create/',
      { method: 'POST', body: data }
    ),

  bulkImportMembers: (file: File, defaultPackageId?: number) => {
    const fd = new FormData();
    fd.append('file', file);
    if (defaultPackageId) fd.append('default_package_id', String(defaultPackageId));
    return request<{
      message: string;
      created_count: number;
      skipped_count: number;
      error_count: number;
      created: { row: number; email: string; name: string; package: string | null }[];
      skipped: { row: number; email: string; reason: string }[];
      errors: { row: number; email: string; error: string }[];
    }>('/accounts/admin/members/bulk-import/', { method: 'POST', body: fd, multipart: true });
  },

  previewMembersImport: (file: File, defaultPackageId?: number) => {
    const fd = new FormData();
    fd.append('file', file);
    if (defaultPackageId) fd.append('default_package_id', String(defaultPackageId));
    return request<{
      summary: { valid: number; skip: number; error: number; total_amount: string; currency: string };
      valid: {
        row: number; name: string; email: string; phone: string;
        country: string; company_name: string; business_category: string;
        package_id: number; package_name: string; package_price: string; package_currency: string;
        start_date: string | null;
      }[];
      skip: { row: number; email: string; name: string; reason: string }[];
      error: { row: number; email: string; name: string; error: string }[];
    }>('/accounts/admin/members/preview-import/', { method: 'POST', body: fd, multipart: true });
  },

  getStats: () => request<AdminStats>('/accounts/admin/stats/'),

  listInquiries: (status: 'pending' | 'completed' | 'failed' | 'all' = 'pending') =>
    request<InquiryItem[]>(`/accounts/admin/inquiries/?status=${status}`),

  confirmInquiry: (id: number, source: 'application' | 'payment' = 'application') =>
    request<{ message: string; reference?: string; payment?: PaymentRecord }>(
      `/accounts/admin/inquiries/${id}/confirm/`,
      { method: 'POST', body: { source } },
    ),

  cancelInquiry: (id: number, source: 'application' | 'payment' = 'application') =>
    request<{ message: string; reference?: string; payment?: PaymentRecord }>(
      `/accounts/admin/inquiries/${id}/cancel/`,
      { method: 'POST', body: { source } },
    ),

  listPayments: (params?: { source?: string; q?: string; limit?: number; status?: 'completed' | 'all' }) => {
    const qs = new URLSearchParams();
    if (params?.source) qs.set('source', params.source);
    if (params?.q) qs.set('q', params.q);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const tail = qs.toString();
    return request<AdminPaymentsResponse>(
      `/accounts/admin/payments/${tail ? `?${tail}` : ''}`,
    );
  },
};

export type PaymentSourceKey =
  | 'all' | 'membership' | 'verification' | 'event'
  | 'marketplace' | 'promotion' | 'investments';

export interface AdminPaymentItem {
  id: string;
  source: Exclude<PaymentSourceKey, 'all'>;
  source_label: string;
  reference: string;
  amount: string;
  currency: string;
  channel: string;
  channel_detail: string;
  confirmation_code: string;
  tracking_id: string;
  paid_at: string | null;
  created_at: string | null;
  payer: {
    id: number | null;
    name: string;
    email: string;
    phone: string;
    role: string;
    company_name: string;
  };
  description: string;
  status: string;
  raw_status: string;
}

export interface AdminPaymentsResponse {
  selected_source: PaymentSourceKey;
  sources: { key: PaymentSourceKey; label: string; count: number }[];
  totals: Record<string, string>;
  items: AdminPaymentItem[];
}

export interface AdminCreateMemberData {
  name: string;
  email: string;
  phone: string;
  country?: string;
  company_name?: string;
  business_category?: string;
  password?: string;
  package_id?: number;
  amount_paid?: string;   // partial OK; defaults to package price
  joined_date?: string;   // YYYY-MM-DD or ISO datetime; defaults to now
}

export interface AdminCreateMerchantData {
  name: string;
  email: string;
  phone: string;
  country?: string;
  company_name?: string;
  business_category?: string;
  password?: string;
  merchant_package_id?: number;
}

// Types
export interface User {
  id: number;
  email: string;
  name: string;
  phone: string;
  role: 'admin' | 'member' | 'staff' | 'vendor' | 'investor' | 'employer' | 'affiliate';
  country: string;
  company_name: string;
  business_category: string;
  is_active: boolean;
  is_email_verified: boolean;
  merchant_package: number | null;
  merchant_package_name: string | null;
  is_paid: boolean;
  is_verified: boolean;
  verified_at: string | null;
  can_crowdfund: boolean;
  can_post_jobs: boolean;
  is_affiliate: boolean;
  affiliate_code: string | null;
  slug: string | null;
  bio: string;
  logo: string | null;
  logo_url: string | null;
  cover_image: string | null;
  cover_image_url: string | null;
  amount_paid: string;
  package_price: string;
  balance: string;
  currency: string;
  last_payment_at: string | null;
  created_at: string;
  updated_at: string;
}

// Admin-only fields accepted on PATCH /accounts/users/<id>/
export interface UserAdminPatch {
  joined_date?: string;       // ISO datetime; overrides created_at
  amount_paid_set?: string;   // sets sum of completed payments to this value
}

export interface PublicSeller {
  id: number;
  name: string;
  company_name: string;
  slug: string | null;
  bio: string;
  country: string;
  business_category: string;
  role: 'admin' | 'member' | 'staff' | 'vendor' | 'investor' | 'employer' | 'affiliate';
  is_verified: boolean;
  verified_at: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
  rating_avg: number | null;
  rating_count: number;
  created_at: string;
}

export interface ReviewItem {
  id: number;
  seller: number;
  rating: number;
  title: string;
  comment: string;
  reviewer_name: string;
  reviewer_email: string;
  created_at: string;
}

export interface ReviewCreateInput {
  rating: number;
  title?: string;
  comment?: string;
  reviewer_name: string;
  reviewer_email?: string;
}

export interface MemberRegisterData {
  name: string;
  email: string;
  phone: string;
  // Required for online (card) payment, omitted for offline (invoice) applications.
  password?: string;
  country: string;
  company_name?: string;
  business_category: string;
  package_id: number;
  payment_method?: 'online' | 'offline';
  // Affiliate referral code captured from the `?ref=` query param.
  ref?: string;
}

export interface PaymentRecord {
  id: number;
  user: number;
  user_email: string;
  package: number;
  package_name: string;
  amount: string;
  reference: string;
  status: 'pending' | 'completed' | 'failed';
  payment_method: string;
  created_at: string;
  updated_at: string;
}

export interface CreateUserData {
  email: string;
  name: string;
  phone?: string;
  role: 'admin' | 'member' | 'staff' | 'vendor' | 'investor' | 'employer' | 'affiliate';
  password: string;
}

export interface ReceiptData {
  receipt_number: string;
  issued_at: string;
  user_name: string;
  user_email: string;
  package_name: string;
  amount: string;
  currency: string;
  reference: string;
  payment_method: string;
  confirmation_code: string;
  paid_at: string;
}

// Packages API
export const packagesApi = {
  listCategories: () =>
    request<PackageCategory[]>('/packages/categories/', { auth: true }),

  createCategory: (data: { name: string; description?: string }) =>
    request<PackageCategory>('/packages/categories/', { method: 'POST', body: data }),

  updateCategory: (id: number, data: { name?: string; description?: string }) =>
    request<PackageCategory>(`/packages/categories/${id}/`, { method: 'PATCH', body: data }),

  deleteCategory: (id: number) =>
    request<void>(`/packages/categories/${id}/`, { method: 'DELETE' }),

  listPackages: (params?: { category?: string; active?: string }) => {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.active) query.set('active', params.active);
    const qs = query.toString();
    return request<PackageItem[]>(`/packages/${qs ? `?${qs}` : ''}`, { auth: true });
  },

  createPackage: (data: CreatePackageData) =>
    request<PackageItem>('/packages/', { method: 'POST', body: data }),

  updatePackage: (id: number, data: Partial<CreatePackageData & { is_active: boolean }>) =>
    request<PackageItem>(`/packages/${id}/`, { method: 'PATCH', body: data }),

  deletePackage: (id: number) =>
    request<void>(`/packages/${id}/`, { method: 'DELETE' }),
};

export interface PackageCategory {
  id: number;
  name: string;
  description: string;
  package_count: number;
  created_at: string;
  updated_at: string;
}

export interface PackageItem {
  id: number;
  category: number;
  category_name: string;
  name: string;
  description: string;
  price: string;
  features: string;
  features_list: string[];
  image: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreatePackageData {
  category: number;
  name: string;
  description?: string;
  price: string;
  features?: string;
  is_active?: boolean;
}

// Merchants API
export const merchantsApi = {
  listPackages: (params?: { active?: string }) => {
    const query = new URLSearchParams();
    if (params?.active) query.set('active', params.active);
    const qs = query.toString();
    return request<MerchantPackageItem[]>(`/merchants/packages/${qs ? `?${qs}` : ''}`, { auth: false });
  },

  createPackage: (data: { name: string; description?: string; benefits?: string; price: string }) =>
    request<MerchantPackageItem>('/merchants/packages/', { method: 'POST', body: data }),

  updatePackage: (id: number, data: Partial<{ name: string; description: string; benefits: string; price: string; is_active: boolean }>) =>
    request<MerchantPackageItem>(`/merchants/packages/${id}/`, { method: 'PATCH', body: data }),

  deletePackage: (id: number) =>
    request<void>(`/merchants/packages/${id}/`, { method: 'DELETE' }),
};

export interface MerchantPackageItem {
  id: number;
  name: string;
  description: string;
  benefits: string;
  benefits_list: string[];
  price: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VendorRegisterData {
  name: string;
  email: string;
  phone: string;
  password: string;
  country: string;
  company_name: string;
  business_category: string;
  merchant_package_id: number;
  payment_method?: 'online' | 'offline';
  // Affiliate referral code captured from the `?ref=` query param.
  ref?: string;
}

export interface AdminStats {
  users: { total: number; active: number; members: number; merchants: number; admins: number };
  pending: { inquiries: number; verifications: number; leads_new: number };
  listings: { products: number; services: number; events: number };
  revenue: {
    total: string;
    this_month: string;
    currency: string;
    completed_payments_count: number;
    orders_paid_count: number;
  };
}

export interface InquiryItem {
  id: number;
  // 'application' = new flow (no User yet); 'payment' = legacy MemberPayment row.
  source: 'application' | 'payment';
  reference: string;
  status: 'pending' | 'completed' | 'failed';
  amount: string;
  currency: string;
  created_at: string;
  paid_at: string | null;
  package: { id: number | null; name: string; category: string };
  user: {
    id: number | null; name: string; email: string; phone: string;
    country: string; company_name: string; role: string;
  };
}

// Marketplace types
export interface ProductCategoryItem { id: number; name: string; description: string; }

export interface ProductSubcategoryItem { id: number; name: string; description: string; category: number; category_name: string; }

export interface ProductImageItem { id: number; image: string; created_at: string; }

export interface ProductItem {
  id: number;
  slug: string;
  owner: number;
  owner_name: string;
  owner_slug: string | null;
  owner_company_name: string;
  owner_country: string;
  owner_is_verified: boolean;
  owner_logo_url: string | null;
  owner_cover_url: string | null;
  name: string;
  description: string;
  short_description: string;
  sku: string;
  category: number | null;
  category_name: string | null;
  subcategory: number | null;
  subcategory_name: string | null;
  brand: string;
  price: string;
  discount_price: string | null;
  currency: string;
  stock_quantity: number;
  stock_status: 'in_stock' | 'out_of_stock';
  low_stock_threshold: number;
  main_image: string | null;
  main_image_url: string | null;
  video_url: string;
  gallery_images: ProductImageItem[];
  sizes: string;
  colors: string;
  materials: string;
  weights: string;
  status: 'draft' | 'published';
  is_featured: boolean;
  warranty_info: string;
  return_policy: string;
  shipping_info: string;
  sector: string;
  trade_interests: string;
  contact_email: string;
  contact_phone: string;
  is_sponsored: boolean;
  sponsored_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceCategoryItem { id: number; name: string; description: string; }

export interface ServiceSubcategoryItem { id: number; name: string; description: string; category: number; category_name: string; }

export interface ServiceItem {
  id: number;
  slug: string;
  owner: number;
  owner_name: string;
  owner_slug: string | null;
  owner_company_name: string;
  owner_country: string;
  owner_is_verified: boolean;
  owner_logo_url: string | null;
  owner_cover_url: string | null;
  name: string;
  description: string;
  short_description: string;
  category: number | null;
  category_name: string | null;
  subcategory: number | null;
  subcategory_name: string | null;
  price: string;
  discount_price: string | null;
  price_type: 'fixed' | 'hourly' | 'per_session';
  currency: string;
  duration: string;
  availability_days: string;
  availability_time: string;
  location_type: 'online' | 'physical';
  location_address: string;
  main_image: string | null;
  main_image_url: string | null;
  video_url: string;
  gallery_images: ProductImageItem[];
  max_bookings_per_day: number | null;
  advance_booking_required: string;
  cancellation_policy: string;
  shipping_info: string;
  status: 'active' | 'inactive';
  is_featured: boolean;
  sector: string;
  trade_interests: string;
  contact_email: string;
  contact_phone: string;
  is_sponsored: boolean;
  sponsored_until: string | null;
  created_at: string;
  updated_at: string;
}

// Leads
export type LeadKind = 'product' | 'service';
export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'won' | 'lost';

export interface LeadItem {
  id: number;
  kind: LeadKind;
  product: number | null;
  product_name: string | null;
  service: number | null;
  service_name: string | null;
  owner: number;
  owner_name: string;
  owner_email: string;
  submitted_by: number | null;
  name: string;
  email: string;
  phone: string;
  country: string;
  message: string;
  status: LeadStatus;
  admin_notes: string;
  owner_notes: string;
  created_at: string;
  updated_at: string;
}

export interface LeadCreateInput {
  kind: LeadKind;
  product?: number | null;
  service?: number | null;
  name: string;
  email: string;
  phone?: string;
  country?: string;
  message: string;
}

// Orders (Sales)
export type OrderKind = 'product' | 'service';
export type OrderPaymentMethod = 'online' | 'cod';
export type OrderStatus = 'pending' | 'paid' | 'processing' | 'fulfilled' | 'cancelled' | 'failed' | 'refunded';

export interface OrderItem {
  id: number;
  reference: string;
  kind: OrderKind;
  product: number | null;
  product_name: string | null;
  service: number | null;
  service_name: string | null;
  owner: number;
  owner_name: string;
  owner_email: string;
  buyer: number | null;
  buyer_name: string;
  buyer_email: string;
  buyer_phone: string;
  buyer_country: string;
  shipping_address: string;
  notes: string;
  quantity: number;
  unit_price: string;
  total_amount: string;
  currency: string;
  payment_method: OrderPaymentMethod;
  status: OrderStatus;
  pesapal_order_tracking_id: string;
  pesapal_payment_method: string;
  pesapal_confirmation_code: string;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderCreateInput {
  kind: OrderKind;
  product?: number | null;
  service?: number | null;
  quantity: number;
  payment_method: OrderPaymentMethod;
  buyer_name: string;
  buyer_email: string;
  buyer_phone?: string;
  buyer_country?: string;
  shipping_address?: string;
  notes?: string;
}

export interface OrderCreateResponse {
  order: OrderItem;
  redirect_url?: string;
  order_tracking_id?: string;
  error?: string;
}

// Marketplace API
export const marketplaceApi = {
  // Categories
  productCategories: () => request<ProductCategoryItem[]>('/marketplace/product-categories/', { auth: false }),
  serviceCategories: () => request<ServiceCategoryItem[]>('/marketplace/service-categories/', { auth: false }),

  // Subcategories (optionally filtered by ?category=<id>)
  productSubcategories: (categoryId?: number | string) =>
    request<ProductSubcategoryItem[]>(
      `/marketplace/product-subcategories/${categoryId ? `?category=${categoryId}` : ''}`,
      { auth: false },
    ),
  serviceSubcategories: (categoryId?: number | string) =>
    request<ServiceSubcategoryItem[]>(
      `/marketplace/service-subcategories/${categoryId ? `?category=${categoryId}` : ''}`,
      { auth: false },
    ),

  // Products
  listProducts: (mine?: boolean) => {
    const q = mine ? '?mine=1' : '';
    return request<ProductItem[]>(`/marketplace/products/${q}`);
  },
  getProduct: (id: number) => request<ProductItem>(`/marketplace/products/${id}/`),
  getProductBySlug: (slug: string) =>
    request<ProductItem>(`/marketplace/products/by-slug/${encodeURIComponent(slug)}/`, { auth: false }),
  createProduct: (data: FormData) =>
    request<ProductItem>('/marketplace/products/', { method: 'POST', body: data, multipart: true }),
  updateProduct: (id: number, data: FormData) =>
    request<ProductItem>(`/marketplace/products/${id}/`, { method: 'PATCH', body: data, multipart: true }),
  deleteProduct: (id: number) =>
    request<void>(`/marketplace/products/${id}/`, { method: 'DELETE' }),
  uploadProductGallery: (productId: number, images: File[]) => {
    const fd = new FormData();
    images.forEach(img => fd.append('images', img));
    return request<{ created: { id: number; image: string }[] }>(
      `/marketplace/products/${productId}/gallery/`,
      { method: 'POST', body: fd, multipart: true }
    );
  },
  deleteProductImage: (imageId: number) =>
    request<void>(`/marketplace/product-images/${imageId}/`, { method: 'DELETE' }),

  // Services
  listServices: (mine?: boolean) => {
    const q = mine ? '?mine=1' : '';
    return request<ServiceItem[]>(`/marketplace/services/${q}`);
  },
  getService: (id: number) => request<ServiceItem>(`/marketplace/services/${id}/`),
  getServiceBySlug: (slug: string) =>
    request<ServiceItem>(`/marketplace/services/by-slug/${encodeURIComponent(slug)}/`, { auth: false }),
  createService: (data: FormData) =>
    request<ServiceItem>('/marketplace/services/', { method: 'POST', body: data, multipart: true }),
  updateService: (id: number, data: FormData) =>
    request<ServiceItem>(`/marketplace/services/${id}/`, { method: 'PATCH', body: data, multipart: true }),
  deleteService: (id: number) =>
    request<void>(`/marketplace/services/${id}/`, { method: 'DELETE' }),
  uploadServiceGallery: (serviceId: number, images: File[]) => {
    const fd = new FormData();
    images.forEach(img => fd.append('images', img));
    return request<{ created: { id: number; image: string }[] }>(
      `/marketplace/services/${serviceId}/gallery/`,
      { method: 'POST', body: fd, multipart: true }
    );
  },
  deleteServiceImage: (imageId: number) =>
    request<void>(`/marketplace/service-images/${imageId}/`, { method: 'DELETE' }),

  // Leads
  createLead: (input: LeadCreateInput) =>
    request<LeadItem>('/marketplace/leads/', { method: 'POST', body: input, auth: false }),
  listLeads: () => request<LeadItem[]>('/marketplace/leads/'),
  getLead: (id: number) => request<LeadItem>(`/marketplace/leads/${id}/`),
  updateLead: (id: number, patch: Partial<Pick<LeadItem, 'status' | 'admin_notes' | 'owner_notes'>>) =>
    request<LeadItem>(`/marketplace/leads/${id}/`, { method: 'PATCH', body: patch }),
  deleteLead: (id: number) =>
    request<void>(`/marketplace/leads/${id}/`, { method: 'DELETE' }),

  // Orders (Sales)
  createOrder: (input: OrderCreateInput) =>
    request<OrderCreateResponse>('/marketplace/orders/create/', { method: 'POST', body: input, auth: false }),
  listOrders: () => request<OrderItem[]>('/marketplace/orders/'),
  getOrder: (id: number) => request<OrderItem>(`/marketplace/orders/${id}/`),
  updateOrder: (id: number, patch: Partial<Pick<OrderItem, 'status' | 'notes'>>) =>
    request<OrderItem>(`/marketplace/orders/${id}/`, { method: 'PATCH', body: patch }),
  orderCallback: (params: { OrderTrackingId?: string; OrderMerchantReference?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ status: OrderStatus; reference: string; order: OrderItem }>(
      `/marketplace/orders/callback/?${q}`, { auth: false }
    );
  },

  // Ad packages
  listAdPackages: () => request<AdPackageItem[]>('/marketplace/ad-packages/', { auth: false }),
  createAdPackage: (input: AdPackageInput) =>
    request<AdPackageItem>('/marketplace/ad-packages/', { method: 'POST', body: input }),
  updateAdPackage: (id: number, patch: Partial<AdPackageInput>) =>
    request<AdPackageItem>(`/marketplace/ad-packages/${id}/`, { method: 'PATCH', body: patch }),
  deleteAdPackage: (id: number) =>
    request<void>(`/marketplace/ad-packages/${id}/`, { method: 'DELETE' }),

  // Promotions
  listPromotions: () => request<PromotionItem[]>('/marketplace/promotions/'),
  createPromotion: (input: PromotionCreateInput) =>
    request<PromotionCreateResponse>('/marketplace/promotions/create/', { method: 'POST', body: input }),
  cancelPromotion: (id: number) =>
    request<PromotionItem>(`/marketplace/promotions/${id}/cancel/`, { method: 'POST' }),
  promotionCallback: (params: { OrderTrackingId?: string; OrderMerchantReference?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ status: PromotionStatus; reference: string; promotion: PromotionItem }>(
      `/marketplace/promotions/callback/?${q}`, { auth: false }
    );
  },
};

// Ad packages & promotions types
export interface AdPackageItem {
  id: number;
  name: string;
  description: string;
  days: number;
  fee_per_day: string;
  currency: string;
  total_price: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdPackageInput {
  name: string;
  description?: string;
  days: number;
  fee_per_day: string;
  currency?: string;
  is_active?: boolean;
}

export type PromotionKind = 'product' | 'service';
export type PromotionStatus = 'pending' | 'active' | 'expired' | 'cancelled' | 'failed';

export interface PromotionItem {
  id: number;
  reference: string;
  kind: PromotionKind;
  product: number | null;
  product_name: string | null;
  service: number | null;
  service_name: string | null;
  owner: number;
  owner_name: string;
  owner_email: string;
  package: number;
  package_name: string;
  days: number;
  fee_per_day: string;
  total_amount: string;
  currency: string;
  payment_method: 'online';
  status: PromotionStatus;
  starts_at: string | null;
  ends_at: string | null;
  is_currently_active: boolean;
  pesapal_order_tracking_id: string;
  pesapal_payment_method: string;
  pesapal_confirmation_code: string;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PromotionCreateInput {
  kind: PromotionKind;
  product?: number | null;
  service?: number | null;
  package: number;
}

export interface PromotionCreateResponse {
  promotion: PromotionItem;
  redirect_url?: string;
  order_tracking_id?: string;
}

// ---------- Events ----------
export type EventStatus = 'draft' | 'published' | 'cancelled';
export type EventType = 'physical' | 'virtual' | 'hybrid';
export type EventOrderStatus = 'pending' | 'paid' | 'cancelled' | 'failed' | 'refunded';

export interface EventCategoryItem { id: number; name: string; description: string; }

export interface EventTicketItem {
  id: number;
  event: number;
  ticket_type: 'standard' | 'vip' | 'early_bird' | 'free' | 'other';
  name: string;
  description: string;
  price: string;
  early_bird_price: string | null;
  currency: string;
  // Dual pricing: the KES figure the organiser quoted for this same tier, set
  // by hand (no FX conversion). Null when the tier sells in `currency` only.
  price_kes: string | null;
  early_bird_price_kes: string | null;
  // Currencies the buyer may choose between at checkout, base currency first.
  currencies: string[];
  quantity_available: number;
  quantity_sold: number;
  remaining: number;
  is_free: boolean;
  is_vip: boolean;
  // Sold as an extra, not an entry ticket — kept out of the listing "from" price.
  is_addon: boolean;
  sort_order: number;
  // Offers AAMECC's M-Pesa Buy Goods till alongside Pesapal on this tier.
  till_payment_enabled: boolean;
  // Amount to quote for till payments, e.g. "USD 52 / KES 6,500".
  till_amount_display: string;
  created_at: string;
  updated_at: string;
}

export interface EventTicketInput {
  id?: number;
  ticket_type?: EventTicketItem['ticket_type'];
  name: string;
  description?: string;
  price: string;
  early_bird_price?: string | null;
  currency?: string;
  price_kes?: string | null;
  early_bird_price_kes?: string | null;
  quantity_available: number;
  is_free?: boolean;
  is_vip?: boolean;
  is_addon?: boolean;
  sort_order?: number;
}

export interface EventImageItem { id: number; image: string; created_at: string; }

export interface EventSpeakerItem {
  id: number;
  name: string;
  title: string;
  organization: string;
  bio: string;
  photo: string | null;
  photo_url: string | null;
  linkedin_url: string;
  twitter_url: string;
  website_url: string;
  is_keynote: boolean;
  sort_order: number;
}

export type EventSponsorTier = 'platinum' | 'gold' | 'silver' | 'bronze' | 'supporting';

export interface EventSponsorItem {
  id: number;
  name: string;
  tier: EventSponsorTier;
  tier_display: string;
  logo: string | null;
  logo_url: string | null;
  website_url: string;
  description: string;
  sort_order: number;
}

export interface EventPartnerItem {
  id: number;
  name: string;
  role: string;
  logo: string | null;
  logo_url: string | null;
  website_url: string;
  sort_order: number;
}

export interface EventAgendaItemItem {
  id: number;
  day_label: string;
  day_date: string | null;
  start_time: string | null;
  end_time: string | null;
  title: string;
  description: string;
  location: string;
  speakers: number[];
  speaker_names: string[];
  sort_order: number;
}

export interface EventItem {
  id: number;
  owner: number;
  owner_name: string;
  owner_email: string;
  title: string;
  slug: string;
  short_description: string;
  description: string;
  category: number | null;
  category_name: string | null;
  tags: string;
  event_type: EventType;
  status: EventStatus;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  time_zone: string;
  registration_deadline: string | null;
  venue_name: string;
  address: string;
  city: string;
  country: string;
  google_maps_link: string;
  online_meeting_link: string;
  banner_image: string | null;
  banner_image_url: string | null;
  promo_video_url: string;
  brochure: string | null;
  brochure_url: string | null;
  gallery_images: EventImageItem[];
  tickets: EventTicketItem[];
  speakers: EventSpeakerItem[];
  sponsors: EventSponsorItem[];
  partners: EventPartnerItem[];
  agenda_items: EventAgendaItemItem[];
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

export interface EventOrderItem {
  id: number;
  reference: string;
  event: number;
  event_title: string;
  ticket: number | null;
  ticket_name: string | null;
  owner: number;
  owner_name: string;
  owner_email: string;
  buyer: number | null;
  buyer_name: string;
  buyer_email: string;
  buyer_phone: string;
  buyer_country: string;
  notes: string;
  quantity: number;
  unit_price: string;
  total_amount: string;
  currency: string;
  payment_method: 'online' | 'offline' | 'free';
  status: EventOrderStatus;
  pesapal_order_tracking_id: string;
  pesapal_payment_method: string;
  pesapal_confirmation_code: string;
  paid_at: string | null;
  confirmation_sent_at: string | null;
  reminders_opted_out: boolean;
  reminder_count: number;
  last_reminder_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface EventOrderCreateInput {
  event: number;
  ticket: number;
  quantity: number;
  buyer_name: string;
  buyer_email: string;
  buyer_phone?: string;
  buyer_country?: string;
  notes?: string;
  // 'offline' = Pay Later — records the registration and emails the details to
  // the registrations inbox instead of redirecting to Pesapal. Ignored for free
  // tickets. Defaults to 'online'.
  payment_method?: 'online' | 'offline';
  // Which of the ticket's quoted currencies the buyer chose to pay in.
  // Defaults to the tier's base currency when omitted.
  currency?: string;
  // Affiliate code from a shared event link.
  ref?: string;
  agreed_to_terms: boolean;
}

export interface EventOrderCreateResponse {
  order: EventOrderItem;
  redirect_url?: string;
  order_tracking_id?: string;
}

export const eventsApi = {
  listCategories: () => request<EventCategoryItem[]>('/events/categories/', { auth: false }),
  createCategory: (data: { name: string; description?: string }) =>
    request<EventCategoryItem>('/events/categories/', { method: 'POST', body: data }),

  listEvents: (params?: { mine?: boolean; category?: number; event_type?: EventType }) => {
    const q = new URLSearchParams();
    if (params?.mine) q.set('mine', '1');
    if (params?.category) q.set('category', String(params.category));
    if (params?.event_type) q.set('event_type', params.event_type);
    const qs = q.toString();
    return request<EventItem[]>(`/events/${qs ? `?${qs}` : ''}`);
  },
  getEvent: (id: number) => request<EventItem>(`/events/${id}/`),
  getEventBySlug: (slug: string) => request<EventItem>(`/events/slug/${slug}/`, { auth: false }),
  createEvent: (data: FormData) =>
    request<EventItem>('/events/', { method: 'POST', body: data, multipart: true }),
  updateEvent: (id: number, data: FormData) =>
    request<EventItem>(`/events/${id}/`, { method: 'PATCH', body: data, multipart: true }),
  deleteEvent: (id: number) =>
    request<void>(`/events/${id}/`, { method: 'DELETE' }),
  uploadEventGallery: (eventId: number, images: File[]) => {
    const fd = new FormData();
    images.forEach(img => fd.append('images', img));
    return request<{ created: { id: number; image: string }[] }>(
      `/events/${eventId}/gallery/`,
      { method: 'POST', body: fd, multipart: true }
    );
  },
  deleteEventImage: (imageId: number) =>
    request<void>(`/events/images/${imageId}/`, { method: 'DELETE' }),

  // Ticket orders
  createOrder: (input: EventOrderCreateInput) =>
    request<EventOrderCreateResponse>('/events/orders/create/', { method: 'POST', body: input, auth: false }),
  listOrders: () => request<EventOrderItem[]>('/events/orders/'),
  getOrder: (id: number) => request<EventOrderItem>(`/events/orders/${id}/`),
  markOrderPaid: (id: number) =>
    request<EventOrderItem>(`/events/orders/${id}/mark-paid/`, { method: 'POST' }),
  setOrderReminders: (id: number, optedOut: boolean) =>
    request<EventOrderItem>(`/events/orders/${id}/reminders/`, { method: 'POST', body: { opted_out: optedOut } }),
  orderCallback: (params: { OrderTrackingId?: string; OrderMerchantReference?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ status: EventOrderStatus; reference: string; order: EventOrderItem }>(
      `/events/orders/callback/?${q}`, { auth: false }
    );
  },
};

// ESG Readiness Index API
export interface EsgInquiryInput {
  name: string;
  company: string;
  email: string;
  phone?: string;
  sector?: string;
  tier?: string;
  interest?: 'champion18' | 'webinar1' | 'webinar2' | 'guides' | 'general';
  message?: string;
}

export const esgApi = {
  submitInquiry: (input: EsgInquiryInput) =>
    request<{ message: string }>('/accounts/esg/inquiry/', {
      method: 'POST',
      body: input,
      auth: false,
    }),
};

// ---------- Investments module ----------

export interface IndustryItem { id: number; name: string; slug: string; }

export type InvestorType = 'angel' | 'vc' | 'company';
export type InvestorStage = '' | 'idea' | 'startup' | 'growth';

export interface InvestorItem {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  country: string;
  investor_type: InvestorType;
  investor_type_display: string;
  bio: string;
  photo: string | null;
  photo_url: string | null;
  preferred_industries: number[];
  preferred_industries_detail: IndustryItem[];
  min_investment: string | null;
  max_investment: string | null;
  currency: string;
  preferred_stage: InvestorStage;
  preferred_stage_display: string;
  is_active: boolean;
  is_verified: boolean;
  opportunities_count: number;
  created_at: string;
  updated_at: string;
}

export type OpportunityInvestmentType = 'equity' | 'debt' | 'partnership';
export type OpportunityTargetStage = '' | 'startup' | 'growth' | 'expansion';
export type OpportunityStatus = 'open' | 'closed';

export interface OpportunityItem {
  id: number;
  investor: number;
  investor_name: string;
  investor_email: string;
  investor_photo_url: string | null;
  title: string;
  industry: number | null;
  industry_name: string | null;
  location: string;
  investment_type: OpportunityInvestmentType;
  investment_type_display: string;
  total_amount: string;
  minimum_ticket: string | null;
  currency: string;
  short_description: string;
  full_details: string;
  target_stage: OpportunityTargetStage;
  target_stage_display: string;
  preferred_sectors: number[];
  preferred_sectors_detail: IndustryItem[];
  status: OpportunityStatus;
  status_display: string;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

export const investmentsApi = {
  // Industries
  listIndustries: () => request<IndustryItem[]>('/investments/industries/', { auth: false }),
  createIndustry: (name: string) =>
    request<IndustryItem>('/investments/industries/', { method: 'POST', body: { name } }),
  deleteIndustry: (id: number) =>
    request<void>(`/investments/industries/${id}/`, { method: 'DELETE' }),

  // Investors
  listInvestors: () => request<InvestorItem[]>('/investments/investors/'),
  getInvestor: (id: number) => request<InvestorItem>(`/investments/investors/${id}/`),
  createInvestor: (data: FormData) =>
    request<InvestorItem>('/investments/investors/', { method: 'POST', body: data, multipart: true }),
  updateInvestor: (id: number, data: FormData) =>
    request<InvestorItem>(`/investments/investors/${id}/`, { method: 'PATCH', body: data, multipart: true }),
  deleteInvestor: (id: number) =>
    request<void>(`/investments/investors/${id}/`, { method: 'DELETE' }),

  // Opportunities
  listOpportunities: (params?: { investor?: number; status?: OpportunityStatus }) => {
    const q = new URLSearchParams();
    if (params?.investor) q.set('investor', String(params.investor));
    if (params?.status) q.set('status', params.status);
    const qs = q.toString();
    return request<OpportunityItem[]>(`/investments/opportunities/${qs ? `?${qs}` : ''}`);
  },
  getOpportunity: (id: number) => request<OpportunityItem>(`/investments/opportunities/${id}/`),
  createOpportunity: (data: FormData) =>
    request<OpportunityItem>('/investments/opportunities/', { method: 'POST', body: data, multipart: true }),
  updateOpportunity: (id: number, data: FormData) =>
    request<OpportunityItem>(`/investments/opportunities/${id}/`, { method: 'PATCH', body: data, multipart: true }),
  deleteOpportunity: (id: number) =>
    request<void>(`/investments/opportunities/${id}/`, { method: 'DELETE' }),
};

// ---------- Investor self-service portal ----------

export type InterestStage = 'interested' | 'contacted' | 'negotiating' | 'closed' | 'passed';

export interface StartupSummary {
  id: number;
  name: string;
  email: string;
  company_name: string;
  business_category: string;
  country: string;
  role: string;
  slug: string | null;
  bio: string;
  is_verified: boolean;
  logo_url: string | null;
  cover_image_url: string | null;
}

export interface InvestorInterestItem {
  id: number;
  investor: number;
  startup: number;
  startup_detail: StartupSummary;
  listing: number | null;
  listing_title: string | null;
  listing_funding: string | null;
  listing_currency: string | null;
  stage: InterestStage;
  stage_display: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface InvestorDashboardData {
  investor: InvestorItem;
  stats: {
    total_interests: number;
    by_stage: Record<InterestStage, number>;
    available_startups: number;
    opportunities: number;
  };
  recent_listings: StartupListingItem[];
  recent_interests: InvestorInterestItem[];
}

export const investorPortalApi = {
  me: () => request<InvestorItem>('/investments/me/'),
  updateMe: (data: FormData | Record<string, unknown>) =>
    request<InvestorItem>('/investments/me/', {
      method: 'PATCH',
      body: data,
      multipart: data instanceof FormData,
    }),
  dashboard: () => request<InvestorDashboardData>('/investments/me/dashboard/'),
  browseStartups: (params?: { industry?: string; country?: string; q?: string }) => {
    const q = new URLSearchParams();
    if (params?.industry) q.set('industry', params.industry);
    if (params?.country) q.set('country', params.country);
    if (params?.q) q.set('q', params.q);
    const qs = q.toString();
    return request<StartupSummary[]>(`/investments/me/startups/${qs ? `?${qs}` : ''}`);
  },
  listInterests: (stage?: InterestStage) => {
    const qs = stage ? `?stage=${stage}` : '';
    return request<InvestorInterestItem[]>(`/investments/me/interests/${qs}`);
  },
  upsertInterest: (input: {
    startup: number;
    stage?: InterestStage;
    notes?: string;
    listing?: number | null;
  }) =>
    request<InvestorInterestItem>('/investments/me/interests/', {
      method: 'POST', body: input,
    }),
  updateInterest: (id: number, input: { stage?: InterestStage; notes?: string }) =>
    request<InvestorInterestItem>(`/investments/me/interests/${id}/`, {
      method: 'PATCH', body: input,
    }),
  deleteInterest: (id: number) =>
    request<void>(`/investments/me/interests/${id}/`, { method: 'DELETE' }),
};

// ---------- Listings + Deal rooms ----------

export type ListingStatus = 'draft' | 'pending' | 'changes_requested' | 'approved' | 'rejected';
export type DealStageType = 'interested' | 'contacted' | 'negotiating' | 'closed' | 'passed';

export interface InvestmentsSettingsItem {
  id: number;
  vendor_access_fee: string;
  currency: string;
  updated_at: string;
}

export interface MyAccessInfo {
  has_access: boolean;
  fee?: string | null;
  currency?: string | null;
  reason?: string;
  paid_at?: string;
  reference?: string;
}

export type StartupStage = '' | 'idea' | 'startup' | 'growth' | 'expansion';

export interface StartupListingItem {
  id: number;
  owner: number;
  owner_name: string;
  owner_email: string;
  owner_company: string;
  owner_role: string;
  owner_logo_url: string | null;
  title: string;
  industry: number | null;
  industry_name: string | null;
  country: string;
  stage: StartupStage;
  stage_display: string;
  short_description: string;
  traction: string;
  milestones: string;
  financials_summary: string;
  financials_doc: string | null;
  financials_doc_url: string | null;
  pitch_deck: string | null;
  pitch_deck_url: string | null;
  funding_needed: string;
  currency: string;
  status: ListingStatus;
  status_display: string;
  admin_comments: string;
  owner_resubmit_notes: string;
  reviewed_by: number | null;
  reviewed_at: string | null;
  view_count: number;
  interested_count: number;
  forwarded_to: number[];
  created_at: string;
  updated_at: string;
}

export interface DealStageAttachmentItem {
  id: number;
  stage: number;
  file: string | null;
  file_url: string | null;
  label: string;
  created_at: string;
}

export interface DealStageItem {
  id: number;
  deal_room: number;
  stage_type: DealStageType;
  stage_type_display: string;
  title: string;
  description: string;
  occurred_at: string;
  created_by: number | null;
  created_by_name: string | null;
  attachments: DealStageAttachmentItem[];
  created_at: string;
}

export interface DealRoomItem {
  id: number;
  investor: number;
  investor_name: string;
  investor_email: string;
  listing: number | null;
  listing_title: string | null;
  startup: number;
  startup_name: string;
  startup_email: string;
  startup_company: string;
  created_by: number | null;
  is_open: boolean;
  stages: DealStageItem[];
  current_stage: DealStageType | null;
  created_at: string;
  updated_at: string;
}

export type OpportunityInterestStatus =
  | 'pending'
  | 'forwarded'
  | 'investor_interested'
  | 'investor_passed'
  | 'room_created';

export interface OpportunityInterestItem {
  id: number;
  opportunity: number;
  opportunity_title: string;
  opportunity_currency: string;
  opportunity_amount: string;
  investor_id: number;
  investor_name: string;
  investor_email: string;
  entrepreneur: number;
  entrepreneur_name: string;
  entrepreneur_email: string;
  entrepreneur_company: string;
  entrepreneur_role: string;
  listing: number | null;
  listing_title: string | null;
  listing_funding: string | null;
  listing_currency: string | null;
  listing_industry: string | null;
  listing_pitch_deck_url: string | null;
  message: string;
  status: OpportunityInterestStatus;
  status_display: string;
  investor_note: string;
  deal_room: number | null;
  has_deal_room: boolean;
  created_at: string;
  updated_at: string;
}

export const investmentsModuleApi = {
  // settings + access gate
  getSettings: () => request<InvestmentsSettingsItem>('/investments/settings/'),
  updateSettings: (data: Partial<{ vendor_access_fee: string; currency: string }>) =>
    request<InvestmentsSettingsItem>('/investments/settings/', { method: 'PATCH', body: data }),
  myAccess: () => request<MyAccessInfo>('/investments/access/me/'),
  startVendorCheckout: () =>
    request<{ redirect_url: string | null; order_tracking_id?: string; reference?: string; message?: string }>(
      '/investments/access/checkout/', { method: 'POST', body: {} }
    ),
  vendorAccessCallback: (params: { OrderTrackingId?: string; OrderMerchantReference?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ status: string; reference: string; access: unknown }>(
      `/investments/access/callback/?${q}`, { auth: false }
    );
  },

  // listings
  listListings: (params?: {
    scope?: 'mine' | 'all';
    status?: ListingStatus;
    forwarded?: '1';
    industry?: number | string;
    country?: string;
    stage?: StartupStage | '';
    min_funding?: string | number;
    max_funding?: string | number;
    q?: string;
  }) => {
    const q = new URLSearchParams();
    if (params?.scope) q.set('scope', params.scope);
    if (params?.status) q.set('status', params.status);
    if (params?.forwarded) q.set('forwarded', params.forwarded);
    if (params?.industry) q.set('industry', String(params.industry));
    if (params?.country) q.set('country', params.country);
    if (params?.stage) q.set('stage', params.stage);
    if (params?.min_funding) q.set('min_funding', String(params.min_funding));
    if (params?.max_funding) q.set('max_funding', String(params.max_funding));
    if (params?.q) q.set('q', params.q);
    const qs = q.toString();
    return request<StartupListingItem[]>(`/investments/listings/${qs ? `?${qs}` : ''}`);
  },
  getListing: (id: number) => request<StartupListingItem>(`/investments/listings/${id}/`),
  createListing: (data: FormData) =>
    request<StartupListingItem>('/investments/listings/', { method: 'POST', body: data, multipart: true }),
  updateListing: (id: number, data: FormData) =>
    request<StartupListingItem>(`/investments/listings/${id}/`, { method: 'PATCH', body: data, multipart: true }),
  deleteListing: (id: number) =>
    request<void>(`/investments/listings/${id}/`, { method: 'DELETE' }),
  submitListing: (id: number, notes?: string) =>
    request<StartupListingItem>(`/investments/listings/${id}/submit/`, { method: 'POST', body: { notes: notes || '' } }),
  approveListing: (id: number, comments?: string) =>
    request<StartupListingItem>(`/investments/listings/${id}/approve/`, { method: 'POST', body: { comments: comments || '' } }),
  reviewListing: (id: number, decision: 'rejected' | 'changes', comments: string) =>
    request<StartupListingItem>(`/investments/listings/${id}/reject/`, { method: 'POST', body: { decision, comments } }),
  forwardListing: (id: number, body: { investors?: number[]; all?: boolean }) =>
    request<{ forwarded: number }>(`/investments/listings/${id}/forward/`, { method: 'POST', body }),
  trackListingView: (id: number) =>
    request<{ view_count: number }>(`/investments/listings/${id}/track-view/`, { method: 'POST' }),

  // deal rooms
  listDealRooms: () => request<DealRoomItem[]>('/investments/deal-rooms/'),
  getDealRoom: (id: number) => request<DealRoomItem>(`/investments/deal-rooms/${id}/`),
  createDealRoom: (data: { investor: number; startup: number; listing?: number | null }) =>
    request<DealRoomItem>('/investments/deal-rooms/', { method: 'POST', body: data }),
  addDealRoomStage: (roomId: number, data: FormData) =>
    request<DealStageItem>(`/investments/deal-rooms/${roomId}/stages/`, { method: 'POST', body: data, multipart: true }),

  // admin: investor interests queue
  listAdminInterests: (params?: { stage?: InterestStage; pending?: '1' }) => {
    const q = new URLSearchParams();
    if (params?.stage) q.set('stage', params.stage);
    if (params?.pending) q.set('pending', params.pending);
    const qs = q.toString();
    return request<(InvestorInterestItem & { has_deal_room: boolean })[]>(
      `/investments/admin/interests/${qs ? `?${qs}` : ''}`
    );
  },
  openRoomFromInterest: (interestId: number, listingId?: number | null) =>
    request<DealRoomItem>(`/investments/admin/interests/${interestId}/open-room/`, {
      method: 'POST', body: { listing: listingId ?? null },
    }),

  // entrepreneur → opportunity interest flow
  expressOpportunityInterest: (
    opportunityId: number,
    payload: { message: string; listing?: number | null },
  ) =>
    request<OpportunityInterestItem>(`/investments/opportunities/${opportunityId}/interest/`, {
      method: 'POST',
      body: {
        message: payload.message,
        ...(payload.listing ? { listing: payload.listing } : {}),
      },
    }),
  forwardOpportunityInterest: (id: number) =>
    request<OpportunityInterestItem>(`/investments/admin/opportunity-interests/${id}/forward/`, {
      method: 'POST', body: {},
    }),
  myOpportunityInterests: () =>
    request<OpportunityInterestItem[]>('/investments/me/opportunity-interests/'),
  myReceivedOpportunityInterests: (status?: OpportunityInterestStatus) => {
    const qs = status ? `?status=${status}` : '';
    return request<OpportunityInterestItem[]>(`/investments/me/received-opportunity-interests/${qs}`);
  },
  decideOpportunityInterest: (id: number, decision: 'interested' | 'pass', note?: string) =>
    request<OpportunityInterestItem>(`/investments/me/received-opportunity-interests/${id}/decide/`, {
      method: 'POST', body: { decision, note: note || '' },
    }),
  listAdminOpportunityInterests: (params?: { status?: OpportunityInterestStatus; ready?: '1' }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.ready) q.set('ready', params.ready);
    const qs = q.toString();
    return request<OpportunityInterestItem[]>(`/investments/admin/opportunity-interests/${qs ? `?${qs}` : ''}`);
  },
  openRoomFromOpportunityInterest: (id: number) =>
    request<{ interest: OpportunityInterestItem; deal_room: DealRoomItem }>(
      `/investments/admin/opportunity-interests/${id}/open-room/`,
      { method: 'POST', body: {} }
    ),

  // admin: vendor access management
  listVendorAccess: () =>
    request<{
      items: {
        id: number; user: number; user_name: string; user_email: string;
        user_role: string; status: 'pending' | 'completed' | 'failed';
        amount: string; currency: string; reference: string;
        paid_at: string | null; created_at: string;
      }[];
      summary: { fee: string; currency: string; total_paid: string; completed_count: number };
    }>('/investments/admin/access/'),
  grantAccess: (user_id: number, note?: string) =>
    request<{ id: number; status: string; user: number }>('/investments/admin/access/grant/', {
      method: 'POST', body: { user_id, note: note || '' },
    }),
  revokeAccess: (user_id: number) =>
    request<{ deleted: number }>(`/investments/admin/access/${user_id}/`, { method: 'DELETE' }),
  listGrantableUsers: (q?: string) => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return request<{
      id: number; name: string; email: string; role: string;
      company_name: string; country: string; has_access: boolean;
    }[]>(`/investments/admin/access/grantable-users/${qs}`);
  },
};

// ESG inquiries (admin queue)
export interface EsgInquiryRow {
  id: number; name: string; company: string; email: string; phone: string;
  sector: string; tier: string; interest: string; interest_label: string;
  message: string; handled: boolean; admin_notes: string;
  created_at: string; updated_at: string;
}

export const esgAdminApi = {
  list: (handled?: 'all' | 'open' | 'done') => {
    const q = handled === 'open' ? '?handled=0' : handled === 'done' ? '?handled=1' : '';
    return request<EsgInquiryRow[]>(`/accounts/admin/esg/inquiries/${q}`);
  },
  update: (id: number, patch: { handled?: boolean; admin_notes?: string }) =>
    request<{ id: number; handled: boolean; admin_notes: string }>(
      `/accounts/admin/esg/inquiries/${id}/`,
      { method: 'PATCH', body: patch }
    ),
  delete: (id: number) =>
    request<void>(`/accounts/admin/esg/inquiries/${id}/`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Jobs & Opportunities Portal
// ---------------------------------------------------------------------------

export type EmployerPaymentStatus = 'unpaid' | 'deposit' | 'paid';

export interface EmployerItem {
  id: number;
  reference: string;
  user: number;
  user_name?: string;
  user_email?: string;
  company_name: string;
  company_email: string;
  phone: string;
  industry: string;
  country: string;
  description: string;
  website: string;
  logo: string | null;
  logo_url: string | null;
  cover_image: string | null;
  cover_image_url: string | null;
  slug: string;
  max_jobs: number;
  total_amount: string;
  amount_paid: string;
  currency: string;
  payment_status: EmployerPaymentStatus;
  payment_status_display?: string;
  payment_notes: string;
  balance_due: string;
  active_jobs_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PublicEmployerItem {
  id: number;
  company_name: string;
  industry: string;
  country: string;
  description: string;
  website: string;
  logo_url: string | null;
  cover_image_url: string | null;
  slug: string;
  active_jobs_count: number;
  created_at: string;
}

export type JobType = 'full_time' | 'part_time' | 'contract' | 'temporary' | 'internship';
export type JobExperienceLevel = 'entry' | 'junior' | 'mid' | 'senior' | 'lead' | 'executive';
export type JobStatus = 'draft' | 'published' | 'closed';

export interface JobCategoryItem {
  id: number;
  name: string;
  slug: string;
  description: string;
  is_active: boolean;
  sort_order: number;
}

export interface JobItem {
  id: number;
  reference: string;
  employer: number;
  employer_name: string;
  employer_logo_url: string | null;
  employer_slug: string;
  employer_industry: string;
  employer_country: string;
  title: string;
  slug: string;
  description: string;
  responsibilities: string;
  requirements: string;
  skills: string;
  skills_list: string[];
  benefits: string;
  job_type: JobType;
  job_type_display: string;
  experience_level: JobExperienceLevel;
  experience_level_display: string;
  category: number | null;
  category_name: string;
  category_label: string;
  vacancies: number;
  country: string;
  city: string;
  is_remote: boolean;
  salary_min: string | null;
  salary_max: string | null;
  currency: string;
  salary_period: string;
  application_deadline: string | null;
  application_email: string;
  apply_url: string;
  job_image: string | null;
  job_image_url: string | null;
  job_document: string | null;
  job_document_url: string | null;
  status: JobStatus;
  status_display: string;
  is_featured: boolean;
  views_count: number;
  applications_count: number;
  created_at: string;
  updated_at: string;
}

export type JobApplicationStatus =
  | 'new' | 'reviewed' | 'shortlisted' | 'rejected' | 'hired';

export interface JobApplicationItem {
  id: number;
  job: number;
  job_title: string;
  job_slug: string;
  employer_name: string;
  applicant_name: string;
  applicant_email: string;
  applicant_phone: string;
  applicant_country: string;
  applicant_city: string;
  cover_letter: string;
  resume: string | null;
  resume_url: string | null;
  linkedin_url: string;
  portfolio_url: string;
  years_of_experience: number | null;
  expected_salary: string;
  status: JobApplicationStatus;
  status_display: string;
  employer_notes: string;
  submitted_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface JobsListParams {
  q?: string;
  job_type?: JobType;
  country?: string;
  remote?: boolean;
  employer?: string;
  scope?: 'public' | 'mine' | 'all';
}

function buildQs(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export const jobsApi = {
  // Jobs (public listing by default)
  listJobs: (params: JobsListParams = {}) =>
    request<JobItem[]>(`/jobs/${buildQs(params)}`, { auth: false }),
  myJobs: () => request<JobItem[]>('/jobs/?scope=mine'),
  adminListAllJobs: () => request<JobItem[]>('/jobs/?scope=all'),
  getJob: (id: number) => request<JobItem>(`/jobs/${id}/`, { auth: false }),
  getJobBySlug: (slug: string) => request<JobItem>(`/jobs/slug/${slug}/`, { auth: false }),
  createJob: (body: Partial<JobItem> | FormData) =>
    request<JobItem>('/jobs/', {
      method: 'POST', body,
      multipart: body instanceof FormData,
    }),
  updateJob: (id: number, body: Partial<JobItem> | FormData) =>
    request<JobItem>(`/jobs/${id}/`, {
      method: 'PATCH', body,
      multipart: body instanceof FormData,
    }),
  listCategories: () =>
    request<JobCategoryItem[]>('/jobs/categories/', { auth: false }),
  deleteJob: (id: number) =>
    request<void>(`/jobs/${id}/`, { method: 'DELETE' }),

  // Employer (self)
  myEmployer: () => request<EmployerItem>('/jobs/me/'),
  updateMyEmployer: (data: FormData) =>
    request<EmployerItem>('/jobs/me/', { method: 'PATCH', body: data, multipart: true }),

  // Public employer pages
  listPublicEmployers: () =>
    request<PublicEmployerItem[]>('/jobs/employers/', { auth: false }),
  getPublicEmployer: (slug: string) =>
    request<PublicEmployerItem>(`/jobs/employers/${slug}/`, { auth: false }),

  // Admin: employer management
  adminListEmployers: (q?: string) => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return request<EmployerItem[]>(`/jobs/admin/employers/${qs}`);
  },
  adminGetEmployer: (id: number) =>
    request<EmployerItem>(`/jobs/admin/employers/${id}/`),
  adminCreateEmployer: (data: FormData) =>
    request<{ message: string; employer: EmployerItem; email_sent: boolean }>(
      '/jobs/admin/employers/create/',
      { method: 'POST', body: data, multipart: true }
    ),
  adminUpdateEmployer: (id: number, data: FormData) =>
    request<EmployerItem>(`/jobs/admin/employers/${id}/`, {
      method: 'PATCH', body: data, multipart: true,
    }),
  adminDeleteEmployer: (id: number) =>
    request<void>(`/jobs/admin/employers/${id}/`, { method: 'DELETE' }),

  // Applications
  apply: (data: FormData) =>
    request<JobApplicationItem>('/jobs/applications/create/', {
      method: 'POST', body: data, multipart: true, auth: false,
    }),
  listApplications: (params: { job?: number; status?: JobApplicationStatus } = {}) =>
    request<JobApplicationItem[]>(`/jobs/applications/${buildQs(params)}`),
  updateApplication: (id: number, body: Partial<JobApplicationItem>) =>
    request<JobApplicationItem>(`/jobs/applications/${id}/`, { method: 'PATCH', body }),
};


// ---------------------------------------------------------------------------
// Crowdfunding
// ---------------------------------------------------------------------------

export type CampaignCategory =
  | 'business' | 'education' | 'charity' | 'startup'
  | 'medical' | 'community' | 'creative' | 'emergency' | 'other';

export type CampaignStatus = 'active' | 'paused' | 'completed' | 'draft';

export interface CampaignImageItem {
  id: number;
  image: string | null;
  image_url: string | null;
  video_url: string;
  caption: string;
  sort_order: number;
  created_at: string;
}

export interface CampaignUpdateItem {
  id: number;
  campaign: number;
  title: string;
  body: string;
  image: string | null;
  image_url: string | null;
  posted_by: number | null;
  posted_by_name: string;
  created_at: string;
}

export type DonationStatus = 'pending' | 'completed' | 'failed' | 'cancelled';
export type PayoutStatus = 'unpaid' | 'paid';

export interface DonationItem {
  id: number;
  reference: string;
  campaign: number;
  campaign_title: string;
  campaign_slug: string;
  donor_name: string;
  donor_email: string;
  donor_phone: string;
  is_anonymous: boolean;
  message: string;
  amount: string;
  currency: string;
  status: DonationStatus;
  status_display: string;
  platform_fee: string;
  platform_fee_percent: string;
  payout_amount: string;
  online_fee: string;
  total_charge: string;
  pesapal_order_tracking_id: string;
  pesapal_payment_method: string;
  pesapal_confirmation_code: string;
  paid_at: string | null;
  payout_status: PayoutStatus;
  payout_status_display: string;
  paid_out_at: string | null;
  paid_out_by: number | null;
  paid_out_by_name: string | null;
  payout_note: string;
  created_at: string;
  updated_at: string;
}

export type FundraiseRequestStatus = 'pending' | 'approved' | 'rejected' | 'converted';

export interface FundraiseRequestItem {
  id: number;
  reference: string;
  full_name: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  title: string;
  category: string;
  target_amount: string;
  currency: string;
  summary: string;
  background: string;
  status: FundraiseRequestStatus;
  status_display: string;
  admin_notes: string;
  reviewed_by: number | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FundraiseRequestPayload {
  full_name: string;
  email: string;
  phone?: string;
  country?: string;
  city?: string;
  title: string;
  category?: string;
  target_amount?: string | number;
  currency?: string;
  summary?: string;
  background?: string;
}

export interface PublicDonationRow {
  id: number;
  public_donor_name: string;
  message: string;
  amount: string;
  currency: string;
  is_anonymous: boolean;
  created_at: string;
}

export interface CampaignItem {
  id: number;
  reference: string;
  beneficiary: number | null;
  beneficiary_user_name: string;
  beneficiary_user_email: string;
  title: string;
  slug: string;
  category: CampaignCategory;
  category_display: string;
  short_description: string;
  full_story: string;
  target_amount: string;
  currency: string;
  amount_raised: string;
  donors_count: number;
  progress_percent: number;
  start_date: string | null;
  end_date: string | null;
  featured_image: string | null;
  featured_image_url: string | null;
  beneficiary_name: string;
  beneficiary_email: string;
  beneficiary_phone: string;
  country: string;
  city: string;
  status: CampaignStatus;
  status_display: string;
  is_featured: boolean;
  views_count: number;
  gallery: CampaignImageItem[];
  updates: CampaignUpdateItem[];
  created_at: string;
  updated_at: string;
}

export interface PublicCampaignItem {
  id: number;
  reference: string;
  title: string;
  slug: string;
  category: CampaignCategory;
  category_display: string;
  short_description: string;
  full_story: string;
  target_amount: string;
  currency: string;
  amount_raised: string;
  donors_count: number;
  progress_percent: number;
  start_date: string | null;
  end_date: string | null;
  featured_image_url: string | null;
  beneficiary_name: string;
  country: string;
  city: string;
  status: CampaignStatus;
  status_display: string;
  is_featured: boolean;
  gallery: CampaignImageItem[];
  updates: CampaignUpdateItem[];
  recent_donations: PublicDonationRow[];
  created_at: string;
}

export interface DonationCreateResponse {
  donation: DonationItem;
  redirect_url: string;
  order_tracking_id: string;
}

export interface DonationStatusResponse {
  reference: string;
  status: DonationStatus;
  amount: string;
  currency: string;
  campaign_slug: string;
  campaign_title: string;
}

export interface DonationCallbackResponse {
  status: DonationStatus;
  reference: string;
  donation: DonationItem;
}

export const crowdfundingApi = {
  // Public
  listPublic: (params: { q?: string; category?: CampaignCategory } = {}) =>
    request<PublicCampaignItem[]>(`/crowdfunding/public/campaigns/${buildQs(params)}`, { auth: false }),
  getPublicBySlug: (slug: string) =>
    request<PublicCampaignItem>(`/crowdfunding/public/campaigns/${slug}/`, { auth: false }),

  // Authenticated (admin sees all, fundraiser sees own)
  listCampaigns: (params: { status?: CampaignStatus; q?: string } = {}) =>
    request<CampaignItem[]>(`/crowdfunding/campaigns/${buildQs(params)}`),
  getCampaign: (id: number) => request<CampaignItem>(`/crowdfunding/campaigns/${id}/`),
  updateCampaign: (id: number, body: FormData | Partial<CampaignItem>) =>
    request<CampaignItem>(`/crowdfunding/campaigns/${id}/`, {
      method: 'PATCH', body, multipart: body instanceof FormData,
    }),
  deleteCampaign: (id: number) =>
    request<void>(`/crowdfunding/campaigns/${id}/`, { method: 'DELETE' }),

  // Admin: create
  adminCreate: (data: FormData) =>
    request<{ message: string; campaign: CampaignItem; email_sent: boolean; temp_password_issued: boolean }>(
      '/crowdfunding/admin/campaigns/create/',
      { method: 'POST', body: data, multipart: true },
    ),

  // Gallery
  deleteImage: (imageId: number) =>
    request<void>(`/crowdfunding/images/${imageId}/`, { method: 'DELETE' }),

  // Updates
  listUpdates: (params: { campaign?: number; slug?: string } = {}) =>
    request<CampaignUpdateItem[]>(`/crowdfunding/updates/${buildQs(params)}`, { auth: false }),
  createUpdate: (body: FormData) =>
    request<CampaignUpdateItem>('/crowdfunding/updates/', {
      method: 'POST', body, multipart: true,
    }),
  deleteUpdate: (id: number) =>
    request<void>(`/crowdfunding/updates/${id}/`, { method: 'DELETE' }),

  // Donations
  listDonations: (params: { campaign?: number; slug?: string; status?: DonationStatus; q?: string } = {}) =>
    request<DonationItem[]>(`/crowdfunding/donations/${buildQs(params)}`),
  markDonationsPaid: (body: { ids: number[]; action?: 'mark_paid' | 'mark_unpaid'; note?: string }) =>
    request<{ updated: number[]; count: number; donations: DonationItem[] }>(
      '/crowdfunding/donations/payout/',
      { method: 'POST', body },
    ),
  donate: (body: {
    campaign_slug: string;
    donor_name?: string;
    donor_email: string;
    donor_phone?: string;
    is_anonymous?: boolean;
    message?: string;
    amount: string | number;
    currency?: string;
  }) =>
    request<DonationCreateResponse>('/crowdfunding/donations/create/', {
      method: 'POST', body, auth: false,
    }),
  donationCallback: (params: { OrderTrackingId: string; OrderMerchantReference: string }) =>
    request<DonationCallbackResponse>(
      `/crowdfunding/donations/callback/${buildQs(params)}`,
      { auth: false },
    ),
  donationStatus: (reference: string) =>
    request<DonationStatusResponse>(`/crowdfunding/donations/status/${reference}/`, { auth: false }),

  // Fundraise requests (public submit + admin queue)
  submitRequest: (body: FundraiseRequestPayload) =>
    request<FundraiseRequestItem>('/crowdfunding/requests/', {
      method: 'POST', body, auth: false,
    }),
  adminListRequests: (params: { status?: FundraiseRequestStatus; q?: string } = {}) =>
    request<FundraiseRequestItem[]>(`/crowdfunding/admin/requests/${buildQs(params)}`),
  adminUpdateRequest: (id: number, body: Partial<FundraiseRequestItem>) =>
    request<FundraiseRequestItem>(`/crowdfunding/admin/requests/${id}/`, {
      method: 'PATCH', body,
    }),
  adminDeleteRequest: (id: number) =>
    request<void>(`/crowdfunding/admin/requests/${id}/`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Affiliates
// ---------------------------------------------------------------------------

export type AffiliateStatus = 'invited' | 'active' | 'disabled';

export interface AffiliateItem {
  id: number;
  code: string;
  link: string;
  name: string;
  email: string;
  phone: string;
  display_name: string;
  display_email: string;
  status: AffiliateStatus;
  notes: string;
  /** Events this affiliate may promote; empty = all events. */
  allowed_event_ids: number[];
  user_id: number | null;
  user_role: string | null;
  has_account: boolean;
  referrals_count: number;
  paid_count: number;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  // Present only on the create response.
  email_sent?: boolean;
  account_created?: boolean;
}

export interface AffiliateReferralItem {
  id: number;
  reference: string;
  affiliate: number;
  affiliate_code: string;
  affiliate_name: string;
  kind: 'registration' | 'event';
  name: string;
  email: string;
  phone: string;
  role: 'member' | 'vendor';
  role_display: string;
  type_label: string;          // "Membership" | "Event"
  package_name: string;        // package name, or event title for events
  amount: string;
  currency: string;
  status: 'pending' | 'paid' | 'cancelled';
  is_paid: boolean;
  paid_at: string | null;
  created_at: string;
  commission_rate: string | null;  // % applied, paid rows only
  earned: string | null;           // affiliate's commission, paid rows only
}

export interface AffiliateCreatePayload {
  mode?: 'existing' | 'new';
  user_id?: number;
  name?: string;
  email?: string;
  phone?: string;
  notes?: string;
  send_invite?: boolean;
}

export const affiliateApi = {
  // Admin
  adminList: (params: { search?: string; status?: AffiliateStatus } = {}) =>
    request<AffiliateItem[]>(`/affiliates/admin/affiliates/${buildQs(params)}`),
  adminCreate: (body: AffiliateCreatePayload) =>
    request<AffiliateItem>('/affiliates/admin/affiliates/', { method: 'POST', body }),
  adminGet: (id: number) => request<AffiliateItem>(`/affiliates/admin/affiliates/${id}/`),
  adminUpdate: (id: number, body: Partial<Pick<AffiliateItem, 'status' | 'name' | 'phone' | 'notes' | 'allowed_event_ids'>>) =>
    request<AffiliateItem>(`/affiliates/admin/affiliates/${id}/`, { method: 'PATCH', body }),
  adminDelete: (id: number) =>
    request<void>(`/affiliates/admin/affiliates/${id}/`, { method: 'DELETE' }),
  // Programme-wide commission (% of each paid registration).
  adminGetSettings: () => request<{ commission_percent: string }>('/affiliates/admin/settings/'),
  adminSetCommissionPercent: (commission_percent: string) =>
    request<{ commission_percent: string }>('/affiliates/admin/settings/', {
      method: 'PUT', body: { commission_percent },
    }),
  adminResendLink: (id: number) =>
    request<{ email_sent: boolean; email: string }>(
      `/affiliates/admin/affiliates/${id}/resend-link/`, { method: 'POST', body: {} },
    ),
  adminListRegistrations: (params: { affiliate?: number; paid?: 'true' | 'false'; kind?: 'registration' | 'event'; search?: string } = {}) =>
    request<AffiliateReferralItem[]>(`/affiliates/admin/registrations/${buildQs(params)}`),

  // Affiliate self-service
  me: () => request<AffiliateItem>('/affiliates/me/'),
  myRegistrations: (params: { paid?: 'true' | 'false'; kind?: 'registration' | 'event' } = {}) =>
    request<AffiliateReferralItem[]>(`/affiliates/me/registrations/${buildQs(params)}`),

  // Public — validate a referral code on the register page
  resolve: (code: string) =>
    request<{ valid: boolean; name?: string; code?: string }>(
      `/affiliates/public/resolve/${encodeURIComponent(code)}/`, { auth: false },
    ),
};


// ---------------------------------------------------------------------------
// Awards & Voting
// ---------------------------------------------------------------------------

export type AwardEventStatus = 'draft' | 'published' | 'archived';
export type NomineeStatus = 'pending' | 'approved' | 'rejected';
export type NominationType = 'individual' | 'business';
export type VoteOrderStatus = 'pending' | 'paid' | 'cancelled' | 'failed';

export interface AwardImageItem {
  id: number;
  image: string;
  image_url: string | null;
  caption: string;
  sort_order: number;
  created_at: string;
}

export interface AwardTierItem {
  id: number;
  event: number;
  title: string;
  description: string;
  entry_fee: string | null;
  currency: string;
  is_active: boolean;
  sort_order: number;
}

export interface AwardSubcategoryItem {
  id: number;
  category: number;
  name: string;
  sort_order: number;
}

export interface AwardCategoryItem {
  id: number;
  event: number;
  name: string;
  description: string;
  image: string | null;
  image_url: string | null;
  sort_order: number;
  subcategories: AwardSubcategoryItem[];
  nominee_count: number;
}

export interface AwardEventItem {
  id: number;
  title: string;
  slug: string;
  short_description: string;
  description: string;
  status: AwardEventStatus;
  start_date: string | null;
  end_date: string | null;
  venue_name: string;
  address: string;
  city: string;
  country: string;
  google_maps_link: string;
  cover_image: string | null;
  cover_image_url: string | null;
  nominations_open: boolean;
  voting_open: boolean;
  /** Server-computed: voting_open and before the deadline. */
  voting_active: boolean;
  voting_ends_at: string | null;
  vote_price: string;
  currency: string;
  winners_announced: boolean;
  is_featured: boolean;
  gallery_images: AwardImageItem[];
  categories: AwardCategoryItem[];
  tiers: AwardTierItem[];
  nominee_count: number;
  created_at: string;
  updated_at: string;
}

export interface NomineeItem {
  id: number;
  slug: string;
  name: string;
  organization: string;
  nomination_type: NominationType;
  country: string;
  photo_url: string | null;
  about: string;
  event: number;
  event_slug: string;
  event_title: string;
  category: number | null;
  category_name: string | null;
  subcategory: number | null;
  subcategory_name: string | null;
  tier: number | null;
  tier_title: string | null;
  is_winner: boolean;
  winner_title: string;
  vote_count: number;
  created_at: string;
  // Admin-only fields
  email?: string;
  phone?: string;
  nominated_by?: 'self' | 'other';
  nominator_name?: string;
  nominator_email?: string;
  nominator_phone?: string;
  status?: NomineeStatus;
}

export interface VoteOrderItem {
  id: number;
  reference: string;
  event: number;
  event_title: string;
  event_slug: string;
  nominee: number;
  nominee_name: string;
  nominee_slug: string;
  voter_name: string;
  voter_email: string;
  voter_phone: string;
  quantity: number;
  unit_price: string;
  total_amount: string;
  currency: string;
  payment_method: 'online' | 'manual';
  status: VoteOrderStatus;
  pesapal_payment_method: string;
  pesapal_confirmation_code: string;
  paid_at: string | null;
  recorded_by_name: string | null;
  notes: string;
  created_at: string;
}

export interface AwardStats {
  event: string;
  currency: string;
  total_votes: number;
  total_revenue: string;
  by_payment_method: { payment_method: string; votes: number; revenue: string }[];
  nominees_by_status: Record<string, number>;
  top_nominees: { id: number; name: string; slug: string; votes_total: number }[];
}

export const awardsApi = {
  // Public
  current: () => request<AwardEventItem>('/awards/current/', { auth: false }),
  getBySlug: (slug: string) => request<AwardEventItem>(`/awards/slug/${slug}/`, { auth: false }),
  listNominees: (params: { event?: string | number; category?: number; subcategory?: number; search?: string; status?: NomineeStatus } = {}) =>
    request<NomineeItem[]>(`/awards/nominees/${buildQs(params)}`, { auth: false }),
  getNomineeBySlug: (slug: string) =>
    request<NomineeItem>(`/awards/nominees/slug/${slug}/`, { auth: false }),
  nominate: (data: FormData) =>
    request<{ message: string; nominee: NomineeItem }>('/awards/nominate/', {
      method: 'POST', body: data, multipart: true, auth: false,
    }),
  vote: (body: {
    nominee?: number;
    nominee_slug?: string;
    quantity: number;
    voter_name?: string;
    voter_email: string;
    voter_phone?: string;
  }) =>
    request<{ order: VoteOrderItem; redirect_url: string; order_tracking_id: string }>(
      '/awards/votes/create/', { method: 'POST', body, auth: false },
    ),
  voteCallback: (params: { OrderTrackingId: string; OrderMerchantReference: string }) =>
    request<{ status: VoteOrderStatus; reference: string; order: VoteOrderItem }>(
      `/awards/votes/callback/${buildQs(params)}`, { auth: false },
    ),

  // Admin — awards editions
  adminList: (params: { status?: AwardEventStatus } = {}) =>
    request<AwardEventItem[]>(`/awards/${buildQs(params)}`),
  adminCreate: (data: FormData) =>
    request<AwardEventItem>('/awards/', { method: 'POST', body: data, multipart: true }),
  adminUpdate: (id: number, body: FormData | Partial<AwardEventItem>) =>
    request<AwardEventItem>(`/awards/${id}/`, {
      method: 'PATCH', body, multipart: body instanceof FormData,
    }),
  adminDelete: (id: number) => request<void>(`/awards/${id}/`, { method: 'DELETE' }),
  stats: (id: number) => request<AwardStats>(`/awards/${id}/stats/`),

  // Admin — gallery
  uploadGallery: (eventId: number, files: File[]) => {
    const fd = new FormData();
    files.forEach(f => fd.append('images', f));
    return request<{ created: AwardImageItem[] }>(`/awards/${eventId}/gallery/`, {
      method: 'POST', body: fd, multipart: true,
    });
  },
  deleteImage: (imageId: number) =>
    request<void>(`/awards/images/${imageId}/`, { method: 'DELETE' }),

  // Admin — tiers / categories / subcategories
  createTier: (body: Partial<AwardTierItem> & { event: number }) =>
    request<AwardTierItem>('/awards/tiers/', { method: 'POST', body }),
  updateTier: (id: number, body: Partial<AwardTierItem>) =>
    request<AwardTierItem>(`/awards/tiers/${id}/`, { method: 'PATCH', body }),
  deleteTier: (id: number) => request<void>(`/awards/tiers/${id}/`, { method: 'DELETE' }),
  createCategory: (body: { event: number; name: string; description?: string; sort_order?: number }) =>
    request<AwardCategoryItem>('/awards/categories/', { method: 'POST', body }),
  updateCategory: (id: number, body: Partial<AwardCategoryItem>) =>
    request<AwardCategoryItem>(`/awards/categories/${id}/`, { method: 'PATCH', body }),
  deleteCategory: (id: number) => request<void>(`/awards/categories/${id}/`, { method: 'DELETE' }),
  createSubcategory: (body: { category: number; name: string; sort_order?: number }) =>
    request<AwardSubcategoryItem>('/awards/subcategories/', { method: 'POST', body }),
  deleteSubcategory: (id: number) => request<void>(`/awards/subcategories/${id}/`, { method: 'DELETE' }),

  // Admin — nominees
  adminListNominees: (params: { event?: string | number; status?: NomineeStatus; search?: string } = {}) =>
    request<NomineeItem[]>(`/awards/nominees/${buildQs(params)}`),
  adminUpdateNominee: (id: number, body: FormData | Partial<NomineeItem>) =>
    request<NomineeItem>(`/awards/nominees/${id}/`, {
      method: 'PATCH', body, multipart: body instanceof FormData,
    }),
  adminDeleteNominee: (id: number) =>
    request<void>(`/awards/nominees/${id}/`, { method: 'DELETE' }),

  // Admin — votes / reconciliation
  listVotes: (params: { event?: string | number; nominee?: number; status?: VoteOrderStatus; payment_method?: 'online' | 'manual' } = {}) =>
    request<VoteOrderItem[]>(`/awards/votes/${buildQs(params)}`),
  recordManualVotes: (body: {
    nominee: number;
    quantity: number;
    voter_name?: string;
    voter_email?: string;
    voter_phone?: string;
    amount?: string | number;
    notes?: string;
  }) =>
    request<{ order: VoteOrderItem; nominee: NomineeItem }>('/awards/votes/manual/', {
      method: 'POST', body,
    }),
};

export interface VoteLogPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: VoteOrderItem[];
}

export const awardsPaymentsApi = {
  logs: (params: { page?: number; page_size?: number; q?: string; status?: VoteOrderStatus; payment_method?: 'online' | 'manual'; event?: string | number } = {}) =>
    request<VoteLogPage>(`/awards/votes/logs/${buildQs(params)}`),
  verify: (id: number, body: { force?: boolean; notes?: string } = {}) =>
    request<{ order: VoteOrderItem; verified: boolean; message: string; nominee?: NomineeItem }>(
      `/awards/votes/${id}/verify/`, { method: 'POST', body },
    ),
};
